import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { expand, map, reduce, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { FundingRequest, FundingRequestAdapter } from '../models/funding-request.model';

type Statuses = 'open' | 'approved' | 'denied';

const CACHE_TTL_MS = 30_000;

@Injectable()
export class FundingRequestService {
    private api: string;
    private listCache = new Map<string, { data: FundingRequest[]; timestamp: number }>();

    constructor(private http: HttpClient, private adapter: FundingRequestAdapter) {
        this.api = `${environment.apiRoot}${environment.apiEndpoints.fundingRequests}`;
    }

    /**
     * Get all Funding Requests with a given status.
     * Results are cached for 30 seconds to prevent duplicate calls within the same page load.
     */
    list(filter: Statuses, minTime?: number, maxTime?: number): Observable<FundingRequest[]> {
        const cacheKey = `${filter}:${minTime ?? ''}:${maxTime ?? ''}`;
        const cached = this.listCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
            return of(cached.data);
        }

        const params = [
            `status=${filter}`
        ];
        if (minTime) {
            params.push(`min=${minTime}`);
        }
        if (maxTime) {
            params.push(`max=${maxTime}`);
        }
        const endpoint = `${this.api}?${params.join('&')}`;

        return this.getItems(endpoint).pipe(
            expand(data => data.next ? this.getItems(endpoint, data.next.id, data.next.time) : of()),
            reduce((acc, data) => acc.concat(data.results), [] as FundingRequest[]),
            tap(data => this.listCache.set(cacheKey, { data, timestamp: Date.now() })),
        );
    }

    private getItems(endpoint: string, startId?: string, startTime?: number): Observable<{next: any, results: FundingRequest[]}> {
        if (startId) {
            endpoint += `&startId=${startId}`;
        }
        if (startTime) {
            endpoint += `&startTime=${startTime}`;
        }
        return this.http.get<any>(endpoint).pipe(
            map(response => {
                let nextIds = null;
                if ('lastEvaluatedKey' in response) {
                    nextIds = {
                        id: response.lastEvaluatedKey.PK,
                        time: response.lastEvaluatedKey.submitTimestamp || response.lastEvaluatedKey.decisionTimestamp || null
                    };
                }
                return {
                    next: nextIds,
                    results: response.applications.map((item: any) => this.adapter.adapt(item))
                };
            })
        );
    }

    /**
     * Get all Funding Requests submitted within a date range across all statuses.
     * Issues three parallel status-filtered queries (open, approved, denied) against
     * the submitTimestamp GSI and merges the results.
     */
    listBySubmitDate(minTime: number, maxTime: number): Observable<FundingRequest[]> {
        const cacheKey = `submit:${minTime}:${maxTime}`;
        const cached = this.listCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
            return of(cached.data);
        }

        const query = (status: Statuses) => {
            const endpoint = `${this.api}?filterBy=submit&status=${status}&min=${minTime}&max=${maxTime}`;
            return this.getItems(endpoint).pipe(
                expand(data => data.next ? this.getItems(endpoint, data.next.id, data.next.time) : of()),
                reduce((acc, data) => acc.concat(data.results), [] as FundingRequest[]),
            );
        };

        return forkJoin([query('open'), query('approved'), query('denied')]).pipe(
            map(([open, approved, denied]) => [...open, ...approved, ...denied]),
            tap(data => this.listCache.set(cacheKey, { data, timestamp: Date.now() })),
        );
    }

    /**
     * Get a single Funding Request
     */
    get(id: string): Observable<FundingRequest> {
        // TDOD: handle lookup by id and requestId (e.g. FYxx-nnnn)
        const endpoint = `${this.api}/${id}`;
        return id ?
            this.http.get<any>(endpoint).pipe(
                // Adapt each item in the raw data array to a FundingRequest object
                map(response => this.adapter.adapt(response.application)),
            ) : of();
    }

    changeChild(id: string, childId: string, version: number): Observable<FundingRequest> {
        const endpoint = `${this.api}/${id}`;
        const data = {
            childId: childId,
            version: version
        };
        return id ?
            this.http.post<any>(endpoint, data).pipe(map(response => this.adapter.adapt(response.application))) : of();
    }

    update(id: string, updateParams: any, version?: number): Observable<FundingRequest> {
        const endpoint = `${this.api}/${id}`;
        if (!('version' in updateParams)) {
            if (version) {
                updateParams['version'] = version;
            } else {
                const message = `No version was provided while trying to update: ${id}`;
                throw new Error(message);
            }
        }
        return id ?
            this.http.post<any>(endpoint, updateParams).pipe(
                map(response => this.adapter.adapt(response.application)),
            ) : of();
    }

    /* Decision Actions - Approve, Deny, Reopen */

    approve(id: string, decisionUser: string, amount: string, categoryId: number, activity: string, version?: number): Observable<FundingRequest> {
        const decisionData = {
            status: 'APPROVED',
            isOpen: null,
            decision: {
                isApproved: true,
                decisionBy: decisionUser,
                timestamp: new Date().getTime(),
                amount: amount,
                categoryId: categoryId,
                approvedActivity: activity
            }
        };
        return this.update(id, decisionData, version);
    }

    deny(id: string, decisionUser: string, version?: number): Observable<FundingRequest> {
        const decisionData = {
            status: 'DENIED',
            isOpen: null,
            decision: {
                isApproved: false,
                decisionBy: decisionUser,
                timestamp: new Date().getTime(),
                amount: null,
                categoryId: -1,
                approvedActivity: null
            }
        };
        return this.update(id, decisionData, version);
    }

    reopen(id: string, version?: number): Observable<FundingRequest> {
        const decisionData = {
            status: null,
            isOpen: 'x',
            decision: {
                isApproved: null,
                decisionBy: null,
                timestamp: null,
                // We specifically do not clear the amount, approvedActivity, and category fields
            }
        };
        return this.update(id, decisionData, version);
    }
}
